import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import PublishIcon from '@material-ui/icons/Publish';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { ENTITLEMENTS as APPENTITLEMENTS } from '../../core/utils/constants';
import ActionText from '../../core/ActionText';
import LanguageSelector from '../../core/LanguageSelector';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import PageLayout from '../../core/PageLayout';
import Paging from '../../core/Paging';
import PagingFilter from '../../core/PagingFilter';
import { UserConsumer } from '../../core/providers/UserProvider';
import { globalStyles } from '../../core/style/gstheme';
import { getGraphFromProject, makeCancelable, sortObjects } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import { PROJECT_COLOURS } from '../utils/constants';
import Projects from './Projects';

const styles = theme => {
  return Object.assign({
    projects: {
      marginTop: 20,
      marginBottom: 20
    },
    createProject: {
      float: 'right',
      paddingTop: '18px',
      paddingBottom: '20px',
      [theme.breakpoints.down('sm')]: {
        float: 'none',
        paddingTop: '0px',
        textAlign: 'center'
      }
    }
  }, globalStyles);
};

const options = [
  {
    name: "Import Project",
    url: 'import',
    entitlement: APPENTITLEMENTS.CAN_IMPORT_PROJECT,
    icon: <PublishIcon />
  }
];

class ProjectsManager extends Component {

  constructor(props) {
    super(props);
    this.state = {
      projects: [],
      isLoadingLanguages: true,
      isLoading: true,
      toIri: null,
      deletedProjects: [],
      lang: "en",
      langs: [],
      isErrored: null,
      text: "",
      page: 1,
      refreshAt: null,
      noneFound: false,
      noResults: true,
      pageCount: null,
    };
    this.cancellable = null;
    this.cancellable1 = null;
  }

  changeLanguage = (lang) => {
    this.setState({
      lang: lang,
      projects: []
    }, () => this.loadProjectInfo());
  };

  loadEntitlements(user, projectIRI) {
    this.cancellable = makeCancelable(GS.getProjectEntitlements(user, projectIRI));
    return this.cancellable;
  }

  checkProjectReady(user, projectIRI) {
    this.check = setTimeout(async () => {
      clearTimeout(this.check);
      try {
        const entitlements = await this.loadEntitlements(user, projectIRI).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (entitlements && entitlements.hasOwnProperty("hasEntitlement") > 0) {
          this.setState({ checking: false, toIri: projectIRI });
        }
        else {
          this.checkProjectReady(user, projectIRI);
        }
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ checking: false, isErrored: e });
      }
    }, 1000);
  }

  createProject = (user) => async (data) => {
    const projectIRIs = GS.getProjectIRIs();
    const projectIRI = projectIRIs.projectIRI;
    const graph = projectIRIs.graphIRI;
    const colourIndex = Math.floor(Math.random() * PROJECT_COLOURS.length);
    const projectColour = PROJECT_COLOURS[colourIndex];
    const newProject = {
      id: projectIRI,
      type: this.props.projectType,
      defaultLang: data.defaultLang,
      projectColour,
      projectLang: [data.defaultLang, ...data.projectLang],
      ...(data.skosXL) && { skosXL: true },
      title: {
        [data.defaultLang]: data.title.trim(),
      },
      hasGroup: [...data.groups],
      description: {
        ...(data.description && data.description.trim() !== "") && { [data.defaultLang]: data.description.trim() }
      }
    };
    await GS.insertResource(newProject, projectIRI, graph);
    this.setState({ checking: true }, () => this.checkProjectReady(user, projectIRI));
  };

  deleteProject = (iri) => {
    const deletedProjectList = [...this.state.deletedProjects];
    deletedProjectList.push(iri);
    GS.deleteResource(iri, getGraphFromProject(iri));
    this.setState({ deletedProjects: deletedProjectList, isLoading: true }, () => this.loadProjectInfo());
  };

  loadProjectInfo = (page) => {
    this.setState({ isLoading: true }, async () => {
      try {
        const data = await this.doSearch(page).promise;
        const sortedProjects = data.results && data.results.length > 0 ? sortObjects([...data.results], 'title', this.state.lang, null, false) : null;
        const projectIDs = sortedProjects ? sortedProjects.map(result => result.id) : null;
        this.setState({
          isLoading: false,
          projects: projectIDs,
          page: data.page,
          pageCount: data.pageCount
        });
      }
      catch (e) {
        if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e, isLoading: false });
      }
    });
  };

  handleFilterChange = (text) => {
    this.setState({ text: text.trim(), page: 1 }, () => this.loadProjectInfo(this.state.page));
  };

  doGetLanguages = () => {
    this.cancellable1 = makeCancelable(GS.getProjectLanguages(this.props.projectClass));
    return this.cancellable1;
  };

  doSearch = (page = 1) => {
    const searchData = {
      search: [
        {
          predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
          value: this.props.projectClass
        },
        {
          predicate: "https://grafsync.graphifi.com/schema/gs/isVersionOfProject",
          invert: true
        },
        {
          predicate: "https://grafsync.graphifi.com/schema/gs/defaultLang",
          value: this.state.lang,
          isExactMatch: true
        }
      ],
      searchMode: "Compliant",
      exclude: this.state.deletedProjects,
      language: this.state.lang,
      predicates: ["http://purl.org/dc/terms/title"],
      sort: ["http://purl.org/dc/terms/title"],
      page,
      pageSize: this.props.pageSize
    };
    if (this.state.text !== "") {
      searchData.search.push({
        predicate: "http://purl.org/dc/terms/title",
        value: this.state.text,
        isExactMatch: false
      });
    }
    this.cancellable = makeCancelable(GS.search(searchData));
    return this.cancellable;
  };

  componentDidMount() {
    this.doGetLanguages().promise
      .then(langs => {
        if (this.cancellable1 && this.cancellable1.isCancelled()) return;
        this.setState({ langs, isLoadingLanguages: false, lang: langs && langs.length > 0 ? langs[0].lang : "en" });
        this.loadProjectInfo();
      })
      .catch((e) => {
        if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ isErrored: e });
      });
  }

  componentWillUnmount() {
    if (this.check) clearTimeout(this.check);
    if (this.cancellable) this.cancellable.cancel();
    if (this.cancellable1) this.cancellable1.cancel();
  }

  render() {

    const { isLoading, projects, toIri, lang, isErrored, text, page, pageCount, checking, langs, isLoadingLanguages } = this.state;
    const { classes, applicationURL, applicationTitle, projectCreator, projectType, entitlement, homeIcon, lockedGraphs } = this.props;
    const langList = langs ? langs.map(langItem => langItem.lang) : [];

    if (toIri) {
      return <Redirect to={applicationURL + '/project/detail#project=' + toIri} />;
    }

    if (isErrored) return <Errored data={isErrored} />;
    if (isLoadingLanguages) return <Loading offset="140px" />;

    const ProjectCreator = projectCreator;

    const pageCrumb = <Switch>
      <Route path={"/"} render={() => <span>Projects Manager</span>} />
      <Route path={"/import"} render={() => <span>Import</span>} />
    </Switch>;

    return (
      <UserConsumer>
        {({ user, entitlements }) => (
          <PageLayout
            title={applicationTitle}
            baseURL={applicationURL}
            graph=""
            id=""
            includeJumps={true}
            options={[...[homeIcon], ...options]}
            layoutTitle={function () { return (pageCrumb); }}
            breadcrumb={{
              baseURL: applicationURL,
              title: applicationTitle,
              static: pageCrumb,
              path: ["import"],
              url: {
                import: "import"
              }
            }}>
            <div style={{ width: 'calc(100% - 30px)', marginTop: 10 }}>
              <Grid container spacing={4} justifyContent="space-evenly">
                <Grid item xs={6} md={4} lg={3}>
                  {langList.length > 0 &&
                    <div style={{ maxWidth: '282px', margin: 'auto' }}>
                      <LanguageSelector
                        label="Filter by Project Language"
                        langs={langList}
                        onSelect={this.changeLanguage}
                        defaultLang={lang} />
                    </div>
                  }
                </Grid>
                <Grid item xs={6} md={4} lg={3}>
                  {langList.length > 0 &&
                    <div style={{ maxWidth: '282px', margin: '3px auto 0px' }}>
                      <PagingFilter
                        label="Filter by Project Title"
                        lang={lang}
                        includeLabel={true}
                        handleFilterChange={this.handleFilterChange}
                        placeholder="Enter text to search on"
                        text={text}
                        maxLength={50} />
                    </div>
                  }
                </Grid>
                <Grid item xs={12} md={4} lg={6}>
                  <div className={classes.createProject}>
                    {entitlements.includes(entitlement) && langList.length > 0 &&
                      <ProjectCreator onComplete={this.createProject(user)} projectType={projectType} />
                    }
                  </div>
                </Grid>
              </Grid>
              {isLoading ?
                <Loading offset="140px" />
                :
                <React.Fragment>
                  {projects && projects.length > 0 ?
                    <Projects
                      applicationURL={applicationURL}
                      entitlement={entitlement}
                      projects={projects}
                      lockedGraphs={lockedGraphs}
                      lang={lang}
                      onDeleteProject={this.deleteProject} />
                    :
                    langList.length > 0 ?
                      <ActionText dataTest="no-projects-found" format="result" offset="120px">No projects exist in the selected language.</ActionText>
                      :
                      <div data-test="get-started-area" style={{ textAlign: 'center' }}>
                        {projectType === "TaxonomyProject" &&
                          <img style={{ height: '140px' }} src="/images/vocabulary-manager-black.svg" alt="" />
                        }
                        {projectType === "OntologyProject" &&
                          <img style={{ height: '140px' }} src="/images/ontology-manager-black.svg" alt="" />
                        }
                        {projectType === "DataProject" &&
                          <img style={{ height: '140px' }} src="/images/data-manager-black.svg" alt="" />
                        }
                        <ActionText dataTest="get-started-projects" offset="30px">Create a project to get started.</ActionText>
                        <div style={{ marginTop: '40px' }}>
                          <ProjectCreator onComplete={this.createProject(user)} projectType={projectType} />
                        </div>
                      </div>
                  }
                  {pageCount > 1 &&
                    <div style={{ marginTop: '60px' }}>
                      <Paging page={page} pageCount={pageCount} pageItemsRange={5} handlePageChange={this.loadProjectInfo} />
                    </div>
                  }
                </React.Fragment>
              }
            </div>
            {checking && <div>
              <Dialog open={true} aria-labelledby="waiting-dialog-title" >
                <DialogTitle
                  disableTypography
                  className={classes.mainHeading}
                  id="waiting-dialog-title">Processing Project</DialogTitle>
                <DialogContent>
                  <DialogContentText className={classes.dialogText}>Please wait. Your project will be ready shortly.</DialogContentText>
                </DialogContent>
              </Dialog>
            </div>
            }
          </PageLayout>
        )}
      </UserConsumer>
    );
  }
}

ProjectsManager.defaultProps = {
  pageSize: 8
};

ProjectsManager.propTypes = {
  classes: PropTypes.object,
  lockedGraphs: PropTypes.array,
  projectClass: PropTypes.string.isRequired,
  pageSize: PropTypes.number.isRequired,
  appBar: PropTypes.object.isRequired,
  projectCreator: PropTypes.object.isRequired,
  projectType: PropTypes.string.isRequired,
  entitlement: PropTypes.string.isRequired,
  additionaData: PropTypes.array,
  applicationTitle: PropTypes.string.isRequired,
  applicationURL: PropTypes.string.isRequired,
  homeIcon: PropTypes.object.isRequired,
  history: PropTypes.object
};

export default withRouter(withStyles(styles)(ProjectsManager));